import {
    Api, Currency, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import { AutogenLayoutCashReceipt } from "./autogen/AutogenLayoutCashReceipt";
import {
    BlurEvent, DataSourceMode, Layout, TableRow, Textbox, PrintableEvent, Snackbar, TableContentsChangedEvent, TableRowCreationEvent, TableSelectionEvent, TableRowDisplayEvent,  LookupModelSelectionEvent, LookupModelSearchEvent, TableRowBeforeSaveEvent, ValidationResult,  ChangeEvent, DataSourceModeChangeEvent } from "@mcleod/components";
import { OpenItemSelection } from "./OpenItemSelection";
import { CommonDialogs } from "@mcleod/common";

export class CashReceipt extends AutogenLayoutCashReceipt {

    private searchResultsMap: Map<number, ModelRow> = new Map();

    onLoad(): void | Promise<void> {
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.tableAppliedItems.rows.length === 0) {
                Snackbar.showWarningSnackbar("No detail amounts entered.", {
                    millisUntilDismissal: 8000,
                    targetPanel: this
                });
                return false;
            }
            return true;
        };
    }

    setItemField(data: any[], tableRow: TableRow) {
        if (data && data.length > 0) {
            if (data.length == 1) {
                const quickAddRow = tableRow;
                this.setQuickAddRowFields(this.createOpenItemRow(data[0]), quickAddRow);
            } else {
                //show popup table to select from
                const layout = Layout.getLayout("lme/ar/cashreceipts/OpenItemSelection") as OpenItemSelection;
                layout.addLayoutLoadListener(() => {
                    const modelRows = data.map(item => {
                        const modelRow = this.createOpenItemRow(item);
                        this.setCustomerLookupData(modelRow, modelRow);
                        return modelRow;
                    });
                    layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, modelRows);
                });

                CommonDialogs.showDialog(layout, {
                    title: "Select Open Item",
                    okProps: {
                        onClick: () => {
                            if (layout.tableOpenItems.selectedRow != null) {
                                const selectedRow = layout.tableOpenItems.selectedRow.data as ModelRow;
                                this.setQuickAddRowFields(this.createOpenItemRow(selectedRow.data), tableRow);
                            }
                        }
                    }
                });
            }
        } else {
            //no items found
        }
    }

    createOpenItemRow(data: any): ModelRow {
        return new ModelRow("lme/ar/open-item", false, data);
    }

    setCustomerLookupData(modelRow: ModelRow, originalModelRow: ModelRow) {
        modelRow.setLookupModelData("customer_id", {
            id: originalModelRow.get("customer_id"),
            name: originalModelRow.get("customer_name")
        });
    }

    setQuickAddRowFields(data: ModelRow, tableRow: TableRow) {

        const tableData = tableRow.data as ModelRow;
        tableData.set("invoice_date", data.get("bill_date"));
        tableData.set("invoice_balance", data.get("amount"));
        tableData.set("customer_id", data.get("customer_id"));
        tableData.set("order_id", data.get("order_id"));
        tableData.set("adjusted_amount", new Currency(0));
        tableData.set("open_item_id", data.get("id"));
        tableData.set("blnum", data.get("reference_number"));
        tableData.set("invoice_no_string", data.get("invoice_no_string"));
        tableData.set("consignee_ref", data.get("consignee_ref"));

        this.setPaymentAmount(tableData);
        this.calculateNewInvoiceBalance(tableRow);
        this.setCustomerLookupData(tableData, data);
        // Store search results in hashmap
        const key = tableData.get("id");
        this.searchResultsMap.set(key, new ModelRow(tableData._modelPath, false, tableData.data));


        tableRow.displayComponentValues();
        this.setQuickAddFields(tableRow, true);
        const textPaymentAmount = tableRow.findComponentById("textboxPaymentAmount") as Textbox;
        textPaymentAmount.focus();

    }

    calculateNewInvoiceBalance(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const invoiceBalance = modelRow.getCurrency("invoice_balance", 0);
        const adjustedAmount = modelRow.getCurrency("adjusted_amount", 0);
        const amount = modelRow.getCurrency("amount", 0);
        invoiceBalance.subtract(amount);
        invoiceBalance.subtract(adjustedAmount);
        modelRow.set("new_inv_balance", invoiceBalance);
        tableRow.displayComponentValues();
    }

    setPaymentAmount(data: ModelRow) {
        const invoiceBalance = data.getCurrency("invoice_balance", 0);
        const appliedAmount = this.activeRow.getCurrency("unapplied_total", 0);
        const amount = invoiceBalance.isLessThan(appliedAmount)
            ? invoiceBalance
            : appliedAmount;
        data.set("amount", amount);
    }

    setQuickAddFields(tableRow: TableRow, value: boolean) {
        (tableRow.findComponentById("textboxPaymentAmount") as Textbox).enabled = value;
        (tableRow.findComponentById("textboxAdjustedAmount") as Textbox).enabled = value;
    }

    searchItems(source: string, key: string, textbox: Textbox) {
        const tableRow = TableRow.getContainingTableRow(textbox);
        if (!StringUtil.isEmptyString(key)) {
            //clear fields
            this.clearTableRow(tableRow);
            this.setQuickAddFields(tableRow, false);
            this.enableAdjustmentFields(tableRow, false);
            Api.search("lme/ar/cashreceipts/open-items", {
                source: source,
                key: key,
                customer_id: this.activeRow.get("customer_id"),
            }).then(response => {
                console.log(response);
                this.setItemField(response.data, tableRow);
            });
        } else {
            this.clearTableRow(tableRow);
            this.setQuickAddFields(tableRow, false);
            this.enableAdjustmentFields(tableRow, false);
            //also clear hashmap entry
            const key = tableRow.data.get("id");
            this.searchResultsMap.delete(key);
        }
    }

    clearTableRow(tableRow: TableRow) {
        const data = tableRow.data as ModelRow;
        data.set("invoice_date", null);
        data.set("invoice_balance", null);
        data.set("blnum", null);
        data.set("invoice_no_string", null);
        data.set("customer_id", null);
        data.set("order_id", null);
        data.set("adjusted_amount", null);
        data.set("new_inv_balance", null);
        data.set("remark", null);
        data.set("open_item_id", null);
        data.set("amount", null);
        data.set("consignee_ref", null);
        tableRow.displayComponentValues();
    }


    setLookupData(textbox: Textbox, id: string, modelRow: ModelRow) {
        modelRow.set(textbox.field, id);
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        lookupLayout.addLayoutLoadListener(() => {
            const filter = { search: id };
            lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setLookupModelData(textbox.field, response.modelRows[0]);
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setLookupModelData(textbox.field, lookupModelData);
                }
                textbox.displayData(modelRow, null, 0);
            });
        })
    }

    /** This is an event handler for the onBlur event of textboxBlnum. */
    textboxBlnumOnBlur(event: BlurEvent) {
        if (this.dataChanged(event, "BL")) {
            const textbox = event.target as Textbox;
            this.searchItems("BL", textbox.text, textbox);
        }
    }

    /** This is an event handler for the onBlur event of textboxInvoiceNo. */
    textboxInvoiceNoOnBlur(event: BlurEvent) {
        if (this.dataChanged(event, "IN")) {
            const textbox = event.target as Textbox;
            this.searchItems("IN", textbox.text, textbox);
        }
    }

    /** This is an event handler for the onBlur event of textboxOrderId. */
    textboxOrderIdOnBlur(event: BlurEvent) {
        if (this.dataChanged(event, "OR")) {
            const textbox = event.target as Textbox;
            const orderId = textbox.text;
            this.searchItems("OR", orderId, textbox);
        }
    }

    /** This is an event handler for the onBlur event of textboxC. */
    textboxCOnBlur(event: BlurEvent) {

        if (this.dataChanged(event, "CO")) {
            const textbox = event.target as Textbox;
            this.searchItems("CO", textbox.text, textbox);
        }
    }

    dataChanged(event: BlurEvent, source: string): boolean {
        const textbox = event.target as Textbox;
        const modelRow = TableRow.getContainingTableRow(textbox).data as ModelRow;
        const id = modelRow.get("id");
        const cachedRow = this.searchResultsMap.get(id);
        if (!cachedRow) {
            return true;
        }
        const fieldMap: Record<string, string> = {
            "BL": "blnum",
            "IN": "invoice_no_string",
            "OR": "order_id",
            "CO": "consignee_ref"
        };

        const field = fieldMap[source];
        if (field && cachedRow.get(field) !== modelRow.get(field)) {
            return true;
        }

        return false;
    }

    /** This is an event handler for the onPrintable event of textboxCustomerBalance. */
    textboxCustomerBalanceOnPrintable(event: PrintableEvent) {
        if (event.printableLabel) {
            event.printableLabel.style.justifyContent = "left";
        }
    }

    /** This is an event handler for the onBlur event of textboxAdjustedAmount. */
    textboxAdjustedAmountOnBlur(event: BlurEvent) {
        const adjustmentTextbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(adjustmentTextbox);
        const data = tableRow.data as ModelRow;
        this.calculateNewInvoiceBalance(tableRow);

        const isZero = data.getCurrency("adjusted_amount", 0).isZero();
        this.enableAdjustmentFields(tableRow, !isZero);
    }

    enableAdjustmentFields(tableRow: TableRow, enable: boolean) {
        const textboxGlid = tableRow.findComponentById("textboxGlid") as Textbox;
        const textboxArReasonCodeId = tableRow.findComponentById("textboxArReasonCodeId") as Textbox;
        const textboxRemark = tableRow.findComponentById("textboxRemark") as Textbox;
        const data = tableRow.data as ModelRow;

        if (enable) {
            textboxGlid.enabled = true;
            textboxGlid.required = true;
            textboxArReasonCodeId.enabled = true;
            textboxRemark.enabled = true;
            if (data.isNull("glid")) {
                this.setLookupData(
                    tableRow.findComponentById("textboxGlid") as Textbox,
                    this.activeRow.get("default_gl_acct"),
                    data);
            }

        } else {
            textboxGlid.clear();
            textboxArReasonCodeId.clear();
            textboxRemark.clear();
            textboxGlid.enabled = false;
            textboxGlid.required = false;
            textboxArReasonCodeId.enabled = false;
            textboxRemark.enabled = false;
        }
    }

    /** This is an event handler for the onContentsChanged event of tableAppliedItems. */
    tableAppliedItemsOnContentsChanged(event: TableContentsChangedEvent) {
        //reset searchMap
        this.searchResultsMap.clear();
        this.tableAppliedItems.rows.forEach(row => {
            const data = row.data as ModelRow;
            const id = data.get("id");
            this.searchResultsMap.set(id, new ModelRow(data._modelPath, false, data.data));
        });
        this.updateUnappliedAmount();
    }

    /** This is an event handler for the onRowCreate event of tableAppliedItems. */
    tableAppliedItemsOnRowCreate(event: TableRowCreationEvent) {
        // have to call this here because onContentsChanged doesn't see the new row in the table yet
        this.updateUnappliedAmount();
    }

    updateUnappliedAmount() {
        if (this.activeRow) {
            const unappliedAmount = new Currency(0);
            const amountPaid = this.activeRow.getCurrency("amount", 0);
            this.tableAppliedItems.rows.forEach(row => {
                unappliedAmount.add(row.data.getCurrency("amount"));
            });
            amountPaid.subtract(unappliedAmount);
            this.activeRow.set("unapplied_total", amountPaid);
        }

    }


    /** This is an event handler for the onBlur event of textboxAmountReceived. */
    textboxAmountReceivedOnBlur(event: BlurEvent) {
        this.updateUnappliedAmount();
    }

    /** This is an event handler for the onSelect event of tableAppliedItems. */
    tableAppliedItemsOnSelect(event: TableSelectionEvent) {
        console.log("Selected row changed in Applied Items table.");
    }

    /** This is an event handler for the onRowDisplay event of tableAppliedItems. */
    tableAppliedItemsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const data = tableRow.data as ModelRow;
        this.setQuickAddFields(tableRow, true);
        const id = data.get("id");
        this.searchResultsMap.set(id, new ModelRow(data._modelPath, false, data.data));

        if (data.getCurrency("adjusted_amount", 0).isZero()) {
            this.enableAdjustmentFields(tableRow, false);
        } else {
            this.enableAdjustmentFields(tableRow, true);
        }
        const textboxAmount = tableRow.findComponentById("textboxPaymentAmount") as Textbox;
        textboxAmount.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            return this.addAmountValidation(textboxAmount, tableRow);
        }
        const textboxAdjustedAmount = tableRow.findComponentById("textboxAdjustedAmount") as Textbox;
        textboxAdjustedAmount.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            return this.addAmountValidation(textboxAdjustedAmount, tableRow);
        }
    }

    private addAmountValidation(textbox: Textbox, tableRow: TableRow): ValidationResult {
        const result: ValidationResult = {
            component: textbox,
            isValid: true
        }
        const data = tableRow.data as ModelRow;
        const amount = data.getCurrency("amount", 0);
        const adjAmount = data.getCurrency("adjusted_amount", 0);
        if (amount.isEqualTo(new Currency(0)) && adjAmount.isEqualTo(new Currency(0))) {
            result.isValid = false;
            result.validationMessage = "Payment Amount and Adjustment Amount cannot both be zero";
        }
        return result;
    }


    /** This is an event handler for the onLookupModelSelection event of textboxCustomerId. */
    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection) {
            this.activeRow.set("customer_balance", event.getValueFromSelectedRow("balance"));
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxArReasonCodeId. */
    textboxArReasonCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "%C%";
    }

    /** This is an event handler for the onBlur event of textboxPaymentAmount. */
    textboxPaymentAmountOnBlur(event: BlurEvent) {
        const paymentTextbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(paymentTextbox);
        this.calculateNewInvoiceBalance(tableRow);
    }

    /** This is an event handler for the beforeRowSave event of tableAppliedItems. */
    tableAppliedItemsBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.isNull("amount")) {
            modelRow.set("amount", new Currency(0));
        }
        if (modelRow.isNull("adjusted_amount")) {
            modelRow.set("adjusted_amount", new Currency(0));
        }
        this.calculateNewInvoiceBalance(tableRow);
    }

    /** This is an event handler for the afterModeChange event of sourceCashReceipt. */
    sourceCashReceiptAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
            if (urlProps?.deposit) {
                const depositTextbox = this.textboxDeposit;
                const id = urlProps.deposit;
                const lookupModelData = new ModelRow("lme/ar/cashreceipts/deposits", false);
                lookupModelData.set(depositTextbox.lookupModelResultField, id);
                this.activeRow.setValueAndLookupModelData(depositTextbox.field, id, lookupModelData);
                depositTextbox.displayData(this.activeRow, null, 0);
            }
        }
    }

    /** This is an event handler for the onChange event of textboxAdjustedAmount. */
    textboxAdjustedAmountOnChange(event: ChangeEvent) {
        const adjustmentTextbox = event.target as Textbox;
        if (adjustmentTextbox.hasFocus()) {
            const tableRow = TableRow.getContainingTableRow(adjustmentTextbox);
            //the model doesn't always update when we get here so checking the actual textbox text is accurate
            const textValue = adjustmentTextbox.text;
            const textboxAmount = !isNaN(parseFloat(textValue)) ? new Currency(parseFloat(textValue)) : new Currency(0);
            const isZero = textboxAmount.isZero();
            this.enableAdjustmentFields(tableRow, !isZero && !StringUtil.isEmptyString(adjustmentTextbox.text));
        }
    }

    setNonOpenItemInfo(tableRow: TableRow) {
        const data = tableRow.data as ModelRow;
        data.updateCurrency("adjusted_amount", this.createZeroCurrency());
        data.updateCurrency("amount", this.createZeroCurrency());
        data.updateCurrency("invoice_balance", this.createZeroCurrency());
        data.updateCurrency("new_inv_balance", this.createZeroCurrency());
        const textboxCustomerIdItem = tableRow.findComponentById("textboxCustomerIdItem") as Textbox;
        textboxCustomerIdItem.enabled = true;
        textboxCustomerIdItem.required = true;
        if (!this.activeRow.isNull("customer_id")) {
            data.setValueAndLookupModelData("customer_id", this.activeRow.get("customer_id"), this.activeRow.getLookupModelData("customer_id"));
        }

        this.setQuickAddFields(tableRow, true);
        tableRow.displayComponentValues();

        // Make company code visible and editable for non-open items (must be after displayComponentValues)
        const companyCodeCell = tableRow.cells.find(c => c.id === "tableAppliedItems-0-14");
        const textboxCompanyCode = tableRow.findComponentById("textboxCompanyId") as Textbox;
        if (companyCodeCell) {
            companyCodeCell.visible = true;
        }
        if (textboxCompanyCode) {
            textboxCompanyCode.visible = true;
            textboxCompanyCode.enabled = true;
            textboxCompanyCode.required = false;
        }
    }


    /** This is an event handler for the onClick event of buttonOpenItems. */
    buttonOpenItemsOnClick() {
        SearchOpenItems.showSlideout({
            cashReceiptRow: this.activeRow,
            tableAppliedItems: this.tableAppliedItems,
            tableMisc: this.tableCashReceiptMisc,
            intercompany: this.activeRow?.get("is_intercompany") === "Y",
            currencyType: this.multicurrencyEnabled ? this.activeRow.get("amount_c") : null,
            updateUnappliedAmount: (): { appliedAmount: Currency, unappliedAmount: Currency } => {
                return this.updateUnappliedAmount();
            },
            addRowsToDetailTable: async (rows: ModelRow[]) => {
                await this.addRowToDetailTable(rows);
            },
            removeRowsFromDetailTable: (rows: ModelRow[], removeEntireGroup: boolean) => {
                this.removeRowsFromDetailTable(rows, removeEntireGroup);
            },
            updateExistingDetailRows: (sourceRows: ModelRow[], exactMatch: boolean) => {
                this.updateExistingDetailRows(sourceRows, exactMatch);
            }
        });
    }

    async addRowToDetailTable(rows: ModelRow[]) {
        for (const row of rows) {
            const newRow = await this.sourceCashReceiptDetail.createBlankRow();
            newRow.set("invoice_balance", row.getCurrency("invoice_balance", this.createZeroCurrency()));
            newRow.setValueAndLookupModelData("customer_id", row.get("customer_id"), row.getLookupModelData("customer_id"));
            newRow.set("order_id", row.get("order_id"));
            newRow.set("adjusted_amount", row.getCurrency("adjusted_amount", this.createZeroCurrency()));
            newRow.set("open_item_id", row.get("id"));
            newRow.set("blnum", row.get("reference_number"));
            newRow.set("invoice_no_string", row.get("invoice_no_string"));
            newRow.set("consignee_ref", row.get("consignee_ref"));
            newRow.set("new_inv_balance", row.getCurrency("new_inv_balance", this.createZeroCurrency()));
            newRow.set("invoice_date", row.get("bill_date"));
            newRow.set("amount", row.getCurrency("pay_amount", this.createZeroCurrency()));
            newRow.set("original_invoice_balance", row.getCurrency("original_invoice_balance", this.createZeroCurrency()));
            newRow.set("related_items_count", row.getNumber("related_items_count", 0));
            newRow.set("remark", row.getString("remark", ""));
            newRow.set("ar_reason_code_id", row.getString("ar_reason_code_id", ""));
            newRow.setLookupModelData("ar_reason_code_id", row.getLookupModelData("ar_reason_code_id"));
            newRow.set("glid", row.getString("glid", ""));
            newRow.set("source_string", row.get("source_string"));
            newRow.setLookupModelData("glid", row.getLookupModelData("glid"));
            newRow.set("company_code", row.get("company_code"));
            newRow.setLookupModelData("company_code", { id: row.get("company_code") });
            const result = this.tableAppliedItems.addRow(newRow, null, { addToData: true, display: true });
            result.row.displayComponentValues();
        }
    }

    removeRowsFromDetailTable(rows: ModelRow[], removeEntireGroup: boolean) {
        const keysToRemove = new Set<string>();
        for (const row of rows) {
            const key = removeEntireGroup ? this.buildAppliedItemKey(row) : row.getString("id");
            keysToRemove.add(key);
        }

        // Iterate backwards to avoid index shifting issues when deleting rows
        for (let i = this.tableAppliedItems.rows.length - 1; i >= 0; i--) {
            const tableRow = this.tableAppliedItems.rows[i];
            const data = tableRow.data as ModelRow;
            const key = removeEntireGroup ? this.buildAppliedItemKey(data) : data.getString("id");

            if (keysToRemove.has(key)) {
                this.tableAppliedItems.deleteRow(tableRow);
            }
        }
    }

    // Called from SearchOpenItems when an applied item's payment amount is changed via the Edit button.
    updateExistingDetailRows(sourceRows: ModelRow[], exactMatch: boolean) {
        // Build a map of table rows keyed by their matching criteria for O(1) lookups
        const tableRowMap = new Map<string, TableRow>();
        for (const tableRow of this.tableAppliedItems.rows) {
            const modelRow = tableRow.data as ModelRow;
            const key = exactMatch ? modelRow.getString("id") : this.buildAppliedItemKey(modelRow);
            if (key) {
                tableRowMap.set(key, tableRow);
            }
        }

        // Now iterate through source rows and update matching table rows in O(n) time
        for (const sourceRow of sourceRows) {
            const key = exactMatch ? sourceRow.getString("id") : this.buildAppliedItemKey(sourceRow);
            const tableRow = tableRowMap.get(key);

            if (tableRow) {
                const modelRow = tableRow.data as ModelRow;
                modelRow.set("amount", sourceRow.getCurrency("pay_amount", this.createZeroCurrency()));
                const adjustedAmount = sourceRow.getCurrency("adjusted_amount", this.createZeroCurrency());
                modelRow.set("adjusted_amount", adjustedAmount);
                modelRow.set("remark", sourceRow.getString("remark", ""));
                if (!adjustedAmount.isZero()) {
                    modelRow.set("ar_reason_code_id", sourceRow.getString("ar_reason_code_id", ""));
                    modelRow.setLookupModelData("ar_reason_code_id", sourceRow.getLookupModelData("ar_reason_code_id"));
                    modelRow.set("glid", sourceRow.getString("glid", ""));
                    modelRow.setLookupModelData("glid", sourceRow.getLookupModelData("glid"));
                } else {
                    modelRow.set("ar_reason_code_id", null);
                    modelRow.setLookupModelData("ar_reason_code_id", null);
                    modelRow.set("glid", null);
                    modelRow.setLookupModelData("glid", null);
                }
                this.enableAdjustmentFields(tableRow, !adjustedAmount.isZero());
                this.calculateNewInvoiceBalance(tableRow);
            }
        }
    }

    private isMatchingAppliedItem(row1: ModelRow, row2: ModelRow): boolean {
        return row1.get("order_id") === row2.get("order_id")
            && row1.get("customer_id") === row2.get("customer_id")
            && (this.activeRow?.get("is_intercompany") !== "Y" || row1.get("company_code") === row2.get("company_code"));// If intercompany check against company codes
    }

    /** This is an event handler for the onDataDisplay event of textboxAmountReceivedHeader. */
    textboxAmountReceivedHeaderOnDataDisplay() {
        this.textboxAmountReceivedHeader.align = HorizontalAlignment.RIGHT;
        if (this.textboxAmountReceivedHeader.printableLabel) {
            this.textboxAmountReceivedHeader.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxAmountReceivedHeader.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxAppliedTotal. */
    textboxAppliedTotalOnDataDisplay() {
        this.textboxAppliedTotal.align = HorizontalAlignment.RIGHT;
        if (this.textboxAppliedTotal.printableLabel) {
            this.textboxAppliedTotal.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxAppliedTotal.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxUnappliedTotal. */
    textboxUnappliedTotalOnDataDisplay() {
        this.textboxUnappliedTotal.align = HorizontalAlignment.RIGHT;
        if (this.textboxUnappliedTotal.printableLabel) {
            this.textboxUnappliedTotal.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxUnappliedTotal.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxCustomerBalance. */
    textboxCustomerBalanceOnDataDisplay() {
        this.textboxCustomerBalance.align = HorizontalAlignment.RIGHT;
        if (this.textboxCustomerBalance.printableLabel) {
            this.textboxCustomerBalance.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxCustomerBalance.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the onClick event of buttonAddDeposit. */
    buttonAddDepositOnClick() {
        const layout = Layout.getLayout("lme/ar/cashreceipts/Deposit") as Deposit;
        layout.addLayoutLoadListener(() => {
            if (this.multicurrencyEnabled && this.hasAppliedItemsOrMiscReceipts()) {
                layout.cashReceiptCurrencyType = this.activeRow.getString("amount_c");
            }

            layout.mainDataSource.addAfterExecutionListener(async () => {
                const id = layout.mainDataSource.activeRow.getString("id");
                if (id) {
                    if (this.multicurrencyEnabled) {
                        const depositCurrency = layout.mainDataSource.activeRow.getString("currency_type");
                        const cashReceiptCurrency = layout.cashReceiptCurrencyType;
                        if (depositCurrency && cashReceiptCurrency && depositCurrency !== cashReceiptCurrency) {
                            // User created a deposit in a different currency, so it can't be applied to this receipt
                            return;
                        }
                    }
                    this.setDepositValue(id);
                    await this.sourceDeposits.search({
                        id: id,
                        cash_receipt_to_ignore: this.activeRow.getString("id")
                    });

                    if (this.multicurrencyEnabled) {
                        const rowDeposits = this.sourceDeposits.activeRow;
                        await this.handleDepositChangeForMulticurrency(rowDeposits);
                    }
                }
            });
        });

        //Open Deposit CrudDecorator
        const crudDecorator = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSave: true,
                showSaveAndClose: true,
            }
        });
        crudDecorator.slideIn();
    }

    /** This is an event handler for the beforeExecution event of sourceDeposits. */
    sourceDepositsBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            event.filter.id = this.activeRow.get("batch_id");
        }
    }

    /** This is an event handler for the afterExecution event of sourceCashReceipt. */
    sourceCashReceiptAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            if (!this.activeRow.isNull("batch_id")) {
                this.sourceDeposits.search({
                    id: this.activeRow.get("batch_id"),
                    cash_receipt_to_ignore: this.activeRow.get("id")
                });
            }

            if (this.multicurrencyEnabled) {
                this.configureCurrencySelector();
            }
        }
    }

    /** This is an event handler for the afterModeChange event of sourceDeposits. */
    sourceDepositsAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            this.sourceDeposits.activeRow.clear();
            this.sourceDeposits.displayDataInBoundComponents();
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxDepositAmount. */
    textboxDepositAmountOnDataDisplay() {
        this.textboxDepositAmount.align = HorizontalAlignment.RIGHT;
        if (this.textboxDepositAmount.printableLabel) {
            this.textboxDepositAmount.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxDepositAmount.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxDepositReceiptTotal. */
    textboxDepositReceiptTotalOnDataDisplay() {
        this.textboxDepositReceiptTotal.align = HorizontalAlignment.RIGHT;
        if (this.textboxDepositReceiptTotal.printableLabel) {
            this.textboxDepositReceiptTotal.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxDepositReceiptTotal.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxDepositUnappliedTotal. */
    textboxDepositUnappliedTotalOnDataDisplay() {
        //Always show white for this currency field because of the dark background
        this.textboxDepositUnappliedTotal.printableLabel.color = PRIMARY_REVERSE_COLOR;
        this.textboxDepositUnappliedTotal.align = HorizontalAlignment.RIGHT;
        if (this.textboxDepositUnappliedTotal.printableLabel) {
            this.textboxDepositUnappliedTotal.printableLabel.align = HorizontalAlignment.RIGHT;
            this.textboxDepositUnappliedTotal.captionLabel.align = HorizontalAlignment.RIGHT;
        }
    }

    /** This is an event handler for the afterExecution event of sourceDeposits. */
    async sourceDepositsAfterExecution(event: DataSourceExecutionEvent) {
        // Add cash receipt amounts to deposit fields
        if (event.getAction() === DataSourceAction.SEARCH) {
            if (this.sourceDeposits.activeRow) {
                if (this.multicurrencyEnabled) {
                    const depositRow = this.sourceDeposits.activeRow;
                    this.setMulticurrencyValuesFromDeposit(depositRow);
                }

                this.receiptTotalIgnoringCurrentReceipt = new Currency(this.sourceDeposits.activeRow.getCurrency("receipt_total_ignoring_receipt", this.createZeroCurrency()));
                this.updateDepositAmounts();
            }
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxDeposit. */
    async textboxDepositOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            await this.sourceDeposits.search({
                id: this.activeRow.get("batch_id"),
                cash_receipt_to_ignore: this.activeRow.get("id")
            });
        } else {
            this.sourceDeposits.clear();
            this.sourceDeposits.displayDataInBoundComponents();
        }

        if (this.multicurrencyEnabled) {
            const rowDeposits = event.hasSelection() ? event.selection : null;
            await this.handleDepositChangeForMulticurrency(rowDeposits);
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDeposit. */
    textboxDepositBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.mainDataSource.mode !== DataSourceMode.SEARCH && this.hasAppliedItemsOrMiscReceipts()) {
            event.filter.currency_type = this.activeRow.getString("amount_c");
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxGlidMisc. */
    textboxGlidMiscBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.isactive = "Y";
        const tableRow = TableRow.getContainingTableRow(event.target as any);
        const modelRow = tableRow ? tableRow.data as ModelRow : null;
        const companyId = modelRow ? modelRow.get("company_code") : this.activeRow?.get("company_id");
        if (companyId != null) {
            event.filter.company_override = companyId;
        }
    }

    /** This is an event handler for the beforeExecution event of sourceCashReceipt. */
    sourceCashReceiptBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
            if (!this.sourceDeposits.activeRow) {
                return;
            }
            const depositDate = this.sourceDeposits.activeRow.getDate("receipt_date");
            this.tableCashReceiptMisc.rows.forEach(row => {
                const data = row.data as ModelRow;
                data.set("invoice_date", depositDate);
            });
        }
    }

    labelInvoiceNoOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Label);
        const modelRow = tableRow.data as ModelRow;
        const invoiceNo = modelRow.getString("invoice_no_string", null);
        const source = modelRow.getString("source_string", null);

        // Do nothing if invoice number is blank
        if (StringUtil.isEmptyString(invoiceNo)) {
            return;
        }

        // Source 'A' indicates a miscellaneous invoice
        if (source === "A" && invoiceNo) {
            this.handleMiscInvoiceClick(invoiceNo);
        } else {
            const orderId = modelRow.getString("order_id", null);
            this.handleNonMiscInvoiceClick(orderId);
        }
    }

    private handleMiscInvoiceClick(invoiceNo: string) {
        MiscInvoiceHistoryList.slideUp({
            invoiceNoString: invoiceNo
        });
    }

    private handleNonMiscInvoiceClick(orderId: string | null) {
        if (orderId) {
            OrderTabView.slideUp({ key: orderId, additionalProps: { startOnInvoiceHistory: true } });
        }
    }

    updateRelatedItemsImage(tableRow: TableRow, data: ModelRow) {
        const imageHasRelatedItems = tableRow.findComponentById("imageHasRelatedItems");
        if (imageHasRelatedItems && data.getNumber("related_items_count", 0) > 1) { // > 1 because current item is included
            imageHasRelatedItems.visible = true;
            imageHasRelatedItems.tooltip = UnpostedAppliedItems.getLayoutWithFilter({
                filter: {
                    customer_id: data.get("customer_id"),
                    order_id: data.get("order_id"),
                    receipt_to_exclude: this.activeRow.get("id")
                },
                rowsToManuallyAdd: this.tableAppliedItems.rows.filter(row => {
                    const rowData = row.data as ModelRow;
                    return rowData.get("customer_id") === data.get("customer_id")
                        && rowData.get("order_id") === data.get("order_id") && rowData.get("id") !== data.get("id");
                }).map(row => {
                    const rowData = row.data as ModelRow;
                    rowData.set("deposit_number", this.activeRow.get("batch_id"));
                    rowData.set("ref_number", this.activeRow.get("check_no"));
                    return rowData;
                })
            });
            if (tableRow.isQuickAddRow()) {
                imageHasRelatedItems.marginTop = 12;
            }
        } else if (imageHasRelatedItems) {
            imageHasRelatedItems.visible = false;
            imageHasRelatedItems.tooltip = null;
        }
    }

    // Used to populate the appliedItemMap on initial display. Should not be used for updating the map as rows are added, edited or removed.
    addToAppliedItemMap(modelRow: ModelRow) {
        const key = this.buildAppliedItemKey(modelRow);
        const originalInvoiceBalance = modelRow.getCurrency("original_invoice_balance", this.createZeroCurrency());
        const newInvoiceBalance = modelRow.getCurrency("new_inv_balance", this.createZeroCurrency());
        const relatedItemsCount = modelRow.getNumber("related_items_count", 1); // Default to 1 to count current row
        if (!this.appliedItemMap.has(key)) {
            this.appliedItemMap.set(key, {
                originalInvoiceBalance: originalInvoiceBalance,
                newInvoiceBalance: newInvoiceBalance,
                relatedItemsCount: relatedItemsCount
            });
        }
    }

    // Used to update the appliedItemMap as rows are added, edited or removed.
    async updateAppliedItemMap(modelRow: ModelRow, action: TableAction) {
        const key = this.buildAppliedItemKey(modelRow);
        if (action === TableAction.ADD) {
            if (!this.appliedItemMap.has(key)) {
                this.appliedItemMap.set(key, {
                    originalInvoiceBalance: modelRow.getCurrency("original_invoice_balance", this.createZeroCurrency()),
                    newInvoiceBalance: modelRow.getCurrency("new_inv_balance", this.createZeroCurrency()),
                    relatedItemsCount: modelRow.getNumber("related_items_count", 1) // Default to 1 to count current row
                });
            } else {
                const existingEntry = this.appliedItemMap.get(key);
                if (existingEntry) {
                    existingEntry.newInvoiceBalance = modelRow.getCurrency("new_inv_balance", this.createZeroCurrency());
                    existingEntry.relatedItemsCount += 1;
                }
            }
        } else if (action === TableAction.UPDATE) {
            const existingEntry = this.appliedItemMap.get(key);
            if (existingEntry) {
                const rowInvoiceBalance = modelRow.getCurrency("invoice_balance", this.createZeroCurrency());
                const rowPaymentAmount = modelRow.getCurrency("amount", this.createZeroCurrency());
                const rowAdjustedAmount = modelRow.getCurrency("adjusted_amount", this.createZeroCurrency());
                const newInvoiceBalance = new Currency(rowInvoiceBalance);
                await newInvoiceBalance.subtract(rowPaymentAmount);
                await newInvoiceBalance.subtract(rowAdjustedAmount);
                existingEntry.newInvoiceBalance = newInvoiceBalance;
            }
        } else if (action === TableAction.DELETE) {
            const existingEntry = this.appliedItemMap.get(key);
            if (existingEntry) {
                existingEntry.relatedItemsCount -= 1;
                if (existingEntry.relatedItemsCount <= 0) {
                    this.appliedItemMap.delete(key);
                } else {
                    existingEntry.newInvoiceBalance = modelRow.getCurrency("invoice_balance", this.createZeroCurrency());
                }
            }
        }
    }

    async updateExistingRowForTableChange(tableRow: TableRow, modelRow: ModelRow) {
        const key = this.buildAppliedItemKey(modelRow);
        const appliedItemEntry = this.appliedItemMap.get(key);
        if (appliedItemEntry) {
            modelRow.updateCurrency("new_inv_balance", appliedItemEntry.newInvoiceBalance);
            modelRow.set("related_items_count", appliedItemEntry.relatedItemsCount);

            // Reverse calculate the invoice balance based on the group's new invoice balance and the row's payment and adjustment amounts
            const invoiceBalance = new Currency(appliedItemEntry.newInvoiceBalance);
            const paymentAmount = modelRow.getCurrency("amount", this.createZeroCurrency());
            const adjustedAmount = modelRow.getCurrency("adjusted_amount", this.createZeroCurrency());
            await invoiceBalance.add(paymentAmount);
            await invoiceBalance.add(adjustedAmount);
            modelRow.updateCurrency("invoice_balance", invoiceBalance);
            this.updateRelatedItemsImage(tableRow, modelRow);
        }
    }

    buildAppliedItemKey(modelRow: ModelRow): string {
        const companyId = modelRow.getString("company_code");
        const orderId = modelRow.getString("order_id");
        const customerId = modelRow.getString("customer_id");
        return `${companyId}-${orderId}-${customerId}`;
    }

    textboxGlidBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.isactive = "Y";
        const tableRow = TableRow.getContainingTableRow(event.target as any);
        const modelRow = tableRow ? tableRow.data as ModelRow : null;
        const companyId = modelRow ? modelRow.get("company_code") : this.activeRow?.get("company_id");
        if (companyId != null) {
            event.filter.company_override = companyId;
        }
    }

    /** This is an event handler for the onChange event of textboxCompanyId. */
    textboxCompanyCodeOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        if (tableRow) {
            const modelRow = tableRow.data as ModelRow;
            // Clear adjustment-related fields when company changes
            modelRow.set("glid", null);
            modelRow.set("ar_reason_code_id", null);

            const textboxGlid = tableRow.findComponentById("textboxGlid") as Textbox;
            const textboxArReasonCodeId = tableRow.findComponentById("textboxArReasonCodeId") as Textbox;
            if (textboxGlid) {
                textboxGlid.displayData(modelRow, null, 0);
            }
            if (textboxArReasonCodeId) {
                textboxArReasonCodeId.displayData(modelRow, null, 0);
            }
        }
    }

    /** This is an event handler for the onChange event of textboxCompanyCodeMisc. */
    textboxCompanyCodeMiscOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        if (tableRow) {
            const modelRow = tableRow.data as ModelRow;
            // Clear GL account when company changes
            modelRow.set("glid", null);

            const textboxGlidMisc = tableRow.findComponentById("textboxGlidMisc") as Textbox;
            if (textboxGlidMisc) {
                textboxGlidMisc.displayData(modelRow, null, 0);
            }
        }
    }

    /** Capture current customer id before the lookup opens so we can detect real changes */
    textboxCustomerIdBeforeLookupModelSearch() {
        this.previousCustomerId = this.activeRow.isNull("customer_id") ? null : this.activeRow.get("customer_id");
    }

    /** This is an event handler for the onChange event of switchIntercompany. */
    async switchIntercompanyOnChange() {
        const currentIsIntercompany = this.activeRow?.get("is_intercompany") === "Y";
        const prevIsIntercompany = this.previousIsIntercompany;

        // If changing from intercompany true to false, update all rows to main company
        // The column will be hidden but this ought to ensure that the company_code is set properly
        if (prevIsIntercompany === true && currentIsIntercompany === false) {
            const mainCompanyId = LMECompanySettings.get()?.id;
            if (mainCompanyId) {
                // Update all applied items rows
                for (const row of this.tableAppliedItems.rows) {
                    const modelRow = row.data as ModelRow;
                    modelRow.set("company_code", mainCompanyId);
                    row.displayComponentValues();
                }

                // Update all misc receipts rows
                for (const row of this.tableCashReceiptMisc.rows) {
                    const modelRow = row.data as ModelRow;
                    modelRow.set("company_code", mainCompanyId);
                    row.displayComponentValues();
                }
            }
        } else if (prevIsIntercompany === false && currentIsIntercompany === true) {
            // Update all misc receipts rows to main company when enabling intercompany
            const mainCompanyId = LMECompanySettings.get()?.id;
            const mainCompanyName = LMECompanySettings.get()?.name;
            for (const row of this.tableCashReceiptMisc.rows) {
                const modelRow = row.data as ModelRow;
                modelRow.set("company_code", mainCompanyId);
                const lookupModelData = new ModelRow("lme/ar/intercompany-ar-company-lookup", false, {
                    id: mainCompanyId,
                    name: mainCompanyName
                });
                modelRow.setValueAndLookupModelData("company_code", mainCompanyId, lookupModelData);
                row.displayComponentValues();
            }
        }

        // Refresh customer balance according to new intercompany state
        try {
            await this.refreshCustomerBalanceForCurrentCustomer();
        } catch (e) {
            // ignore errors from balance refresh
        }
        this.updateCompanyCodeColumnVisibility();
    }

    /**
     *
     * Refreshes the customer_balance field on the active row based on intercompany toggle state.
     * When intercompany is enabled, adds cross-reference balances from other companies.
     * When intercompany is disabled, removes cross-reference balances.
     */
    private async refreshCustomerBalanceForCurrentCustomer(): Promise<void> {
        const customerId = this.activeRow ? this.activeRow.get("customer_id") : null;
        if (!customerId) return;

        const currentIsIntercompany = this.activeRow?.get("is_intercompany") === "Y";
        const prevIsIntercompany = this.previousIsIntercompany;

        // Start from the current displayed customer balance
        const currentBalance = this.activeRow.getCurrency("customer_balance", this.createZeroCurrency());

        // Helper to fetch xref balance from the API
        const fetchXref = async (): Promise<Currency> => {
            try {
                const response = await Api.post("lme/ar/get-customer-xref-balance", { customer_id: customerId });
                if (response && response.data && response.data[0] && response.data[0].balance != null) {
                    return new Currency(response.data[0].balance);
                }
            } catch (e) {
                // ignore
            }
            return this.createZeroCurrency();
        };

        // If changed from Intercompany true -> false: remove the xref portion
        if (prevIsIntercompany === true && currentIsIntercompany === false) {
            // Prefer an explicit base balance if present
            if (!this.activeRow.isNull("customer_xref_base_balance")) {
                const base = this.activeRow.getCurrency("customer_xref_base_balance", this.createZeroCurrency());
                this.activeRow.updateCurrency("customer_balance", base);
                this.previousIsIntercompany = currentIsIntercompany;
                return;
            }

            // Otherwise fetch the xref and subtract it from the existing balance
            const xref = await fetchXref();
            const newBalance = new Currency(currentBalance);
            newBalance.subtract(xref);
            this.activeRow.updateCurrency("customer_balance", newBalance);

            this.previousIsIntercompany = currentIsIntercompany;
            return;
        }

        // If changed from Intercompany false -> true: add the xref portion
        if (prevIsIntercompany === false && currentIsIntercompany === true) {
            const xref = await fetchXref();
            const newBalance = new Currency(currentBalance);
            newBalance.add(xref);
            this.activeRow.updateCurrency("customer_balance", newBalance);

            this.previousIsIntercompany = currentIsIntercompany;
            return;
        }
        this.previousIsIntercompany = currentIsIntercompany;
    }

    private updateCompanyCodeColumnVisibility(enableForNonOpenItems: boolean = false) {
        const isIntercompany = this.activeRow?.get("is_intercompany") === "Y";
        const companyCodeColumn = this.tableAppliedItems.getColumnByCaption("Company Code");

        // Update visibility for existing table rows (excluding quick add row)
        this.tableAppliedItems.rows.forEach(row => {
            // Skip quick add row - it's handled separately below
            if (row.isQuickAddRow()) return;

            const companyCodeCell = row.cells.find(c => c.id === "tableAppliedItems-0-14");
            const textboxCompanyCode = row.findComponentById("textboxCompanyId") as Textbox;
            if (companyCodeCell) {
                companyCodeCell.visible = isIntercompany;
            }
            if (textboxCompanyCode) {
                textboxCompanyCode.visible = isIntercompany;
                // Enable the textbox for non-open items when explicitly requested
                if (enableForNonOpenItems) {
                    textboxCompanyCode.enabled = true;
                    textboxCompanyCode.required = false;
                }
            }
        });

        // Update visibility for the applied items quick add row specifically
        if (this.tableAppliedItems.quickAddRow) {
            const quickAddCell = this.tableAppliedItems.quickAddRow.cells.find(c => c.id === "tableAppliedItems-0-14");
            const textboxCompanyCode = this.tableAppliedItems.quickAddRow.findComponentById("textboxCompanyId") as Textbox;
            if (quickAddCell) {
                quickAddCell.visible = isIntercompany;
            }
            if (textboxCompanyCode) {
                textboxCompanyCode.visible = isIntercompany;
                // Clear the company code when becoming visible so it starts blank
                if (enableForNonOpenItems) {
                    textboxCompanyCode.enabled = true;
                    textboxCompanyCode.required = true;
                }
            }
        }

        // Set column visibility to match
        if (companyCodeColumn) {
            this.tableAppliedItems.setColumnVisibility(companyCodeColumn, isIntercompany);
        }

        // Update tableCashReceiptMisc company code visibility
        const companyCodeColumnMisc = this.tableCashReceiptMisc.getColumnByCaption("Company Code");

        // Update visibility for existing misc table rows (excluding quick add row)
        this.tableCashReceiptMisc.rows.forEach(row => {
            // Skip quick add row - it's handled separately below
            if (row.isQuickAddRow()) return;

            const companyCodeCell = row.cells.find(c => c.id === "tableCashReceiptMisc-0-3");
            const textboxCompanyCodeMisc = row.findComponentById("textboxCompanyCodeMisc") as Textbox;
            if (companyCodeCell) {
                companyCodeCell.visible = isIntercompany;
            }
            if (textboxCompanyCodeMisc) {
                textboxCompanyCodeMisc.visible = isIntercompany;
                if (enableForNonOpenItems && isIntercompany) {
                    textboxCompanyCodeMisc.enabled = true;
                    textboxCompanyCodeMisc.required = false;
                }
            }
        });

        // Update visibility for the misc table quick add row
        if (this.tableCashReceiptMisc.quickAddRow) {
            const quickAddCell = this.tableCashReceiptMisc.quickAddRow.cells.find(c => c.id === "tableCashReceiptMisc-0-3");
            const textboxCompanyCodeMisc = this.tableCashReceiptMisc.quickAddRow.findComponentById("textboxCompanyCodeMisc") as Textbox;
            if (quickAddCell) {
                quickAddCell.visible = isIntercompany;
            }
            if (textboxCompanyCodeMisc) {
                textboxCompanyCodeMisc.visible = isIntercompany;
            }
        }

        // Set column visibility to match
        if (companyCodeColumnMisc) {
            this.tableCashReceiptMisc.setColumnVisibility(companyCodeColumnMisc, isIntercompany);
        }
    }

    private configureIntercompanyToggle(): void {
        const arControl = LMEGeneralSettings.getSingleton().getARControl();
        const companySettings = LMECompanySettings.get();

        // Check if the switch should be visible: main cash receipts company AND AR Control intercompany is enabled
        const isMainCashReceiptsCompany = companySettings?.intercompany_main_company === true;
        const isIntercompanyEnabled = arControl?.is_intercompany === 'Y';
        this.switchIntercompany.visible = isMainCashReceiptsCompany && isIntercompanyEnabled;

        // The switch cannot be edited when applied items or misc. receipts exist for a cash receipt
        // In edit mode, disable if applied items or misc receipts exist
        this.switchIntercompany.enabled = !this.hasAppliedItemsOrMiscReceipts();
    }
}


